import React, { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  Button,
} from '@mui/material';
import { ArrowBack as BackIcon, Edit as EditIcon, Check as CheckIcon } from '@mui/icons-material';
import { TimelineProvider, useTimeline, TOOL_MODES } from './TimelineProvider';
import TimelineCanvas from './TimelineCanvas';
import TimelineToolbar from './TimelineToolbar';
import { selectTimelineById, updateTimeline, addTimeline } from './timelinesSlice';
import { getTimelinesUrl } from '../../urls';
import useThemeStyles from '../../hooks/useThemeStyles';
import timelineService from '../../utils/apiRequests/timelineService';

/**
 * Normalize backend timeline data to frontend format
 * Backend uses _id, frontend uses id
 * Backend sends Date objects as ISO strings in JSON, convert to epoch timestamps
 */
const normalizeTimeline = (backendTimeline) => {
  if (!backendTimeline) return null;

  const normalized = {
    ...backendTimeline,
    id: backendTimeline.id || backendTimeline._id?.toString() || backendTimeline._id,
    projectId: backendTimeline.projectId?.toString() || backendTimeline.projectId,
  };

  // Convert Date strings to epoch timestamps
  if (backendTimeline.createdAt) {
    if (typeof backendTimeline.createdAt === 'string') {
      normalized.createdAt = new Date(backendTimeline.createdAt).getTime();
    } else if (backendTimeline.createdAt instanceof Date) {
      normalized.createdAt = backendTimeline.createdAt.getTime();
    } else {
      normalized.createdAt = backendTimeline.createdAt;
    }
  } else {
    normalized.createdAt = Date.now();
  }

  if (backendTimeline.updatedAt) {
    if (typeof backendTimeline.updatedAt === 'string') {
      normalized.updatedAt = new Date(backendTimeline.updatedAt).getTime();
    } else if (backendTimeline.updatedAt instanceof Date) {
      normalized.updatedAt = backendTimeline.updatedAt.getTime();
    } else {
      normalized.updatedAt = backendTimeline.updatedAt;
    }
  } else {
    normalized.updatedAt = Date.now();
  }

  return normalized;
};

/**
 * TimelineEditorContent - The actual editor content (wrapped by provider)
 */
const TimelineEditorContent = () => {
  const { colors, isDark } = useThemeStyles();
  const { timeline, timelineId, setToolMode } = useTimeline();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const debounceTimeoutRef = useRef(null);

  const [isEditingName, setIsEditingName] = React.useState(false);
  const [editName, setEditName] = React.useState(timeline?.name || '');

  // Sync name when timeline changes
  useEffect(() => {
    if (timeline?.name && !isEditingName) {
      setEditName(timeline.name);
    }
  }, [timeline?.name, isEditingName]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSaveName = useCallback(async () => {
    if (editName.trim()) {
      dispatch(updateTimeline({ id: timelineId, name: editName.trim() }));

      // Auto-save name change with debouncing
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await timelineService.updateTimeline(projectId, timelineId, {
            name: editName.trim(),
          });

          if (response.error) {
            console.error('Error auto-saving timeline name:', response.error);
          } else if (response.data?.data) {
            const normalizedTimeline = normalizeTimeline(response.data.data);
            dispatch(updateTimeline(normalizedTimeline));
          }
        } catch (err) {
          console.error('Error auto-saving timeline name:', err);
        }
      }, 1000); // 1 second debounce for name
    }
    setIsEditingName(false);
  }, [dispatch, timelineId, editName, projectId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Tool mode shortcuts
      if (e.key === 'v' || e.key === 'V') {
        setToolMode(TOOL_MODES.CURSOR);
      } else if (e.key === 'h' || e.key === 'H') {
        setToolMode(TOOL_MODES.PAN);
      }

      // Undo/Redo
      if (e.ctrlKey && e.key === 'z') {
        e.preventDefault();
        // Undo is handled by TimelineProvider
      } else if (e.ctrlKey && e.key === 'y') {
        e.preventDefault();
        // Redo is handled by TimelineProvider
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [setToolMode]);

  if (!timeline) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Timeline not found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The timeline you&apos;re looking for doesn&apos;t exist.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: colors.background.primary,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          backgroundColor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <Tooltip title="Back to Timelines">
          <IconButton
            onClick={() => navigate(getTimelinesUrl(projectId))}
            sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'inherit' }}
          >
            <BackIcon />
          </IconButton>
        </Tooltip>

        {isEditingName ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              size="small"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') {
                  setEditName(timeline.name);
                  setIsEditingName(false);
                }
              }}
              sx={{ minWidth: 200 }}
            />
            <IconButton onClick={handleSaveName} size="small" color="primary">
              <CheckIcon />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {timeline.name}
            </Typography>
            <IconButton
              onClick={() => setIsEditingName(true)}
              size="small"
              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Typography variant="caption" color="text.secondary">
          {timeline.notes.length} notes · {timeline.chapterBorders.length} chapters
        </Typography>
      </Box>

      {/* Canvas */}
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <TimelineCanvas />
        <TimelineToolbar />
      </Box>
    </Box>
  );
};

/**
 * TimelineEditorPage - Wrapper that provides the TimelineProvider context
 */
const TimelineEditorPage = ({ timelineId: propTimelineId }) => {
  const { timelineId: paramTimelineId, projectId } = useParams();
  const timelineId = propTimelineId || paramTimelineId;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { colors } = useThemeStyles();

  // Check if timeline exists
  const timeline = useSelector(selectTimelineById(timelineId));
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);

  // Load timeline from backend on mount
  useEffect(() => {
    const loadTimeline = async () => {
      if (!projectId || !timelineId) {
        setLoading(false);
        return;
      }

      // If timeline already exists in Redux, skip loading
      if (timeline) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setNotFound(false);

      try {
        const response = await timelineService.getTimelineById(projectId, timelineId);

        if (response.error) {
          console.error('Error loading timeline:', response.error);
          setNotFound(true);
          setLoading(false);
          return;
        }

        if (response.data?.data) {
          const normalizedTimeline = normalizeTimeline(response.data.data);
          dispatch(addTimeline(normalizedTimeline));
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('Error loading timeline:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, timelineId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: colors.background.primary,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (notFound || !timeline) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: colors.background.primary,
          gap: 2,
        }}
      >
        <Typography sx={{ color: colors.text.secondary }}>
          {notFound ? 'Timeline not found' : 'Loading timeline...'}
        </Typography>
        <Button onClick={() => navigate(getTimelinesUrl(projectId))}>Back to Timelines</Button>
      </Box>
    );
  }

  return (
    <TimelineProvider timelineId={timelineId}>
      <TimelineEditorContent />
    </TimelineProvider>
  );
};

TimelineEditorPage.propTypes = {
  timelineId: PropTypes.string,
};

TimelineEditorPage.defaultProps = {
  timelineId: null,
};

export default TimelineEditorPage;
